import { Button } from '@creditornot/cb-button';
import { Upload } from '@creditornot/cb-icons';
import React, { CSSProperties, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import useUser from 'common/hooks/useUser';
import ScientistError from 'common/illustrations/ScientistError';
import { Typography as T } from 'common/typography';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';

import { XmenuImport } from './XmenuImport';
import YuhoCelebrate from './yuho_celebrate.png';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import { ListContentType } from '../../hooks/useSharedMenuEditorLists';

type Props = {
  listType: ListContentType;
  onStartMenuBootstrap: () => void;
  style?: CSSProperties;
};

const Root = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Illustration = styled.img`
  width: 100%;
  max-width: 375px;
  margin-block-end: 32px;
`;

const StyledScientistError = styled(ScientistError)`
  width: 100%;
  max-width: 375px;
  margin-block-end: 32px;
`;

const EmptyMenuDescriptionAndActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const EmptyMenuDescription = styled(T.Body3)`
  margin-block-end: 24px;
`;

const getEmptyListTitle = (
  isNewSelection: boolean,
  listType: ListContentType,
  isSharedMenu: boolean,
  isSingleVenue: boolean,
) => {
  if (isNewSelection && isSharedMenu) {
    return <FormattedMessage id="listing-manager.selections.editor.new-selection.header" />;
  }

  if (isNewSelection && isSingleVenue) {
    // TODO: Translation key
    return 'You created a new menu';
  }

  if (listType === 'all-options') {
    return <FormattedMessage id="shared-menu.editor.empty-list.no-options" />;
  }
  return <FormattedMessage id="menu.noItems" />;
};

export const EmptyCollection = ({ listType, onStartMenuBootstrap, style }: Props) => {
  const { selection } = useSelectionData();
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedMenu = !vlmeConfig;
  const isSingleVenue = !!vlmeConfig?.isSingleVenue;
  const { isEmployee } = useUser();

  const isNewSelection = useMemo(
    () => selection?.version.number === 1 && !selection?.draft,
    [selection?.draft, selection?.version.number],
  );

  const title = useMemo(
    () => getEmptyListTitle(isNewSelection, listType, isSharedMenu, isSingleVenue),
    [isNewSelection, isSharedMenu, isSingleVenue, listType],
  );

  if (isSharedMenu) {
    return (
      <Root style={style}>
        {isNewSelection ? <Illustration src={YuhoCelebrate} /> : <StyledScientistError />}

        <T.Heading6 style={{ marginBlockEnd: '16px' }}>{title}</T.Heading6>
        {isNewSelection && (
          <EmptyMenuDescriptionAndActions>
            <EmptyMenuDescription>
              <FormattedMessage id="shared-menu.editor.empty-menu.description" />
            </EmptyMenuDescription>
            <Button icon={<Upload />} size="small" variant="lightBlue" onClick={onStartMenuBootstrap}>
              <FormattedMessage id="shared-menu.editor.empty-menu.bootstrap.button-label" />
            </Button>
          </EmptyMenuDescriptionAndActions>
        )}
      </Root>
    );
  }

  if (isSingleVenue) {
    return (
      <Root style={style}>
        {isNewSelection ? <Illustration src={YuhoCelebrate} /> : <StyledScientistError />}

        <T.Heading6 style={{ marginBlockEnd: '16px' }}>{title}</T.Heading6>
        {isNewSelection && isEmployee ? (
          <EmptyMenuDescriptionAndActions>
            <EmptyMenuDescription>
              <FormattedMessage id="listing-manager.selections.venue-editor.xmenu-import.empty-menu-description" />
            </EmptyMenuDescription>
            <XmenuImport />
          </EmptyMenuDescriptionAndActions>
        ) : (
          <>
            {/* TODO: Translation key */}
            Start building your menu by adding an item, option or category
          </>
        )}
      </Root>
    );
  }

  return (
    <Root style={style}>
      <StyledScientistError />

      <T.Heading6 style={{ marginBlockEnd: '16px' }}>{title}</T.Heading6>
      <EmptyMenuDescriptionAndActions>
        <EmptyMenuDescription>
          {/* TODO: Translation keys */}
          Items, category and options you have in the Shared Menu connected to this venue will appear here after the
          Shared Menu gets published
        </EmptyMenuDescription>
      </EmptyMenuDescriptionAndActions>
    </Root>
  );
};
